import React, { useState } from "react"
import { useSelector } from "react-redux"

import {
    MtUserRegisteredCreate,
    MtMailTemplate,
} from "server/models"
import {
    useInput,
    replacePlaceholders,
    TextPlaceholders,
} from "client/utils"
import {
    MAIL_SETTING_FROM_EMAIL,
    MAIL_SETTING_OWNER_EMAIL,
    MAIL_TEMPLATE_CONFIRM_EMAIL,
    MAIL_TEMPLATE_USER_REGISTERED_NOTIFICATION,

    StoreType,
    UserRegisteredValidation,

    LoadUserByLoginOrEmail,
    LoadRegisteredUserByEmail,
    CreateConfirmationCodeByEmail,
    RegisterUser,

    LoadMailSettingByName,
    LoadMailTemplateByName,
    SendMail,
} from "client/store"

import * as routes from "client/parts/routes"
import { Footer } from "client/parts/layout"
import {
    Loader,
    FormGroup,
    Feedback,
    LostConnection,
} from "client/components"
import {
    AuthWrapper,
    AuthTitle,
    AuthFormWrapper,
    AuthForm,
    AuthInput,
    AuthButton,
    SuccessfulAuthWrapper,
    AuthFormDescription,
} from "./styled"

const Register = () => {
    const [firstName, setFirstName] = useInput("")
    const [lastName, setLastName] = useInput("")
    const [email, setEmail] = useInput("")
    const [companyName, setCompanyName] = useInput("")

    const [validation, setValidation] = useState<UserRegisteredValidation>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [isRegistered, setIsRegistered] = useState(false)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const lostServerConnection = useSelector((state: StoreType) => state.system.lostServerConnection)

    const onConfirm = () => {
        setIsSaving(true)

        Promise.all([
            LoadUserByLoginOrEmail(email),
            LoadRegisteredUserByEmail(email),
        ]).then(([existingUser, registeredUser]) => {
            if (existingUser || registeredUser) {
                setValidation({ email: lang.userWithEmailExists })
                setIsSaving(false)
                return
            }

            const confirmationCode = CreateConfirmationCodeByEmail(email)

            const newUser: MtUserRegisteredCreate = {
                email,
                firstName,
                lastName,
                companyName,
                confirmationCode,
            }
            RegisterUser(newUser, lang).then((res) => {
                setValidation(res.validation)

                if (res.validation) {
                    setIsSaving(false)
                    return
                }

                let fromEmail: string
                let ownerEmail: string
                let confirmEmail: MtMailTemplate = null
                let userRegisteredNotification: MtMailTemplate = null

                Promise.all([
                    LoadMailSettingByName(MAIL_SETTING_FROM_EMAIL).then((mailSetting) => {
                        fromEmail = mailSetting ? mailSetting.value : ""
                    }),
                    LoadMailSettingByName(MAIL_SETTING_OWNER_EMAIL).then((mailSetting) => {
                        ownerEmail = mailSetting ? mailSetting.value : ""
                    }),
                    LoadMailTemplateByName(MAIL_TEMPLATE_CONFIRM_EMAIL).then((mailTemplate) => {
                        confirmEmail = mailTemplate
                    }),
                    LoadMailTemplateByName(MAIL_TEMPLATE_USER_REGISTERED_NOTIFICATION).then((mailTemplate) => {
                        userRegisteredNotification = mailTemplate
                    }),
                ]).then(() => {
                    const placeholders: TextPlaceholders = {
                        user: `${firstName} ${lastName}`,
                        email,
                        company: companyName,
                        confirmLink: `${document.location.origin}${routes.confirmEmailRoute}/${confirmationCode}`,
                    }

                    Promise.all([
                        SendMail({ // confirm email
                            from: fromEmail,
                            to: email,
                            subject: confirmEmail ? confirmEmail.subject : "",
                            html: confirmEmail ? replacePlaceholders(confirmEmail.content, placeholders) : "",
                        }),
                        SendMail({ // new user registered
                            from: fromEmail,
                            to: ownerEmail,
                            subject: userRegisteredNotification ? userRegisteredNotification.subject : "",
                            html: userRegisteredNotification ? replacePlaceholders(userRegisteredNotification.content, placeholders) : "",
                        }),
                    ]).then(() => {
                        setIsRegistered(true)
                        setIsSaving(false)
                    })
                })
            })
        })
    }

    const allowSignUp = firstName.length && lastName.length && email.length && companyName.length

    return (
        <AuthWrapper>
            { lostServerConnection && <LostConnection /> }
            { isSaving && <Loader paddingTop={260} /> }

            <AuthFormWrapper show={!isRegistered}>
                <AuthTitle>{lang.signUp}</AuthTitle>
                <AuthForm>
                    <FormGroup>
                        <AuthInput type="text" value={firstName} onChange={setFirstName} placeholder={lang.firstName} />
                        <Feedback>{validation && validation.firstName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <AuthInput type="text" value={lastName} onChange={setLastName} placeholder={lang.lastName} />
                        <Feedback>{validation && validation.lastName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <AuthInput type="text" value={email} onChange={setEmail} placeholder={lang.email} />
                        <Feedback>{validation && validation.email}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <AuthInput type="text" value={companyName} onChange={setCompanyName} placeholder={lang.companyName} />
                        <Feedback>{validation && validation.companyName}</Feedback>
                    </FormGroup>

                    <AuthButton
                        variant="success"
                        disabled={!allowSignUp}
                        onClick={onConfirm}
                    >
                        {lang.getStarted}
                    </AuthButton>
                </AuthForm>
            </AuthFormWrapper>

            <SuccessfulAuthWrapper show={isRegistered}>
                <AuthTitle>{lang.thanksForRegistration}</AuthTitle>
                <AuthFormDescription>{lang.checkAndConfirmEmail}</AuthFormDescription>
            </SuccessfulAuthWrapper>

            <Footer />
        </AuthWrapper>
    )
}
export default Register
